export const getGlobalQuery = (workstationId, fields, window, side) => {
  const sideFields = fields.map((field) => field.startsWith('step_') ? field + '_' + side : field);

  return `
    import "array"
    import "strings"
    import "regexp"
    
    fields = ${JSON.stringify(sideFields)}
    pat   = "^(" + strings.joinStr(arr: fields, v: "|") + ")$"
    re    = regexp.compile(v: pat)
    
    base =
      from(bucket: "engine")
        |> range(start: v.timeRangeStart, stop: v.timeRangeStop)
        |> filter(fn: (r) => r._measurement == "reba_score")
        |> filter(fn: (r) => r._field == "workstation_id" or r._field =~ re)
        |> pivot(rowKey: ["_time"], columnKey: ["_field"], valueColumn: "_value")
        |> filter(fn: (r) => r.workstation_id == ${workstationId})
        |> drop(columns: ["workstation_id"])
    
    // fabrique une table "long" pour un step donné
    make = (col) => 
      base
        |> duplicate(column: col, as: "_value")     // copie la colonne step_X vers _value
        |> filter(fn: (r) => exists r._value)       // sécurité : enlève les null
        |> keep(columns: ["_time","_value"])
        |> set(key: "_field", value: col)           // identifie le step (tag)
    
    union(tables: array.map(arr: fields, fn: (x) => make(col: x)))
      |> group(columns: ["_field"])                 // conserve _field pendant l'aggregation
      |> aggregateWindow(every: ${window}, fn: mean, createEmpty: false)
      |> group(columns: [])                         // enlève _field des clés de groupe
      |> pivot(rowKey: ["_time"], columnKey: ["_field"], valueColumn: "_value")
      |> rename(columns: {_time: "time"})
  `;
};

export const getQuery = (workstationId, fields, window, side) => {
  const sideFields = fields.map((field) => field.startsWith('step_') ? field + '_' + side : field);

  return `
    import "array"
    import "strings"
    import "regexp"
    
    fields = ${JSON.stringify(sideFields)}
    pat    = "^(" + strings.joinStr(arr: fields, v: "|") + ")$"
    re     = regexp.compile(v: pat)
    
    base =
      from(bucket: "engine")
        |> range(start: v.timeRangeStart, stop: v.timeRangeStop)
        |> filter(fn: (r) => r._measurement == "reba_score")
        |> filter(fn: (r) => r._field == "workstation_id" or r._field =~ re)
        |> pivot(rowKey: ["_time"], columnKey: ["_field"], valueColumn: "_value")
        |> filter(fn: (r) => r.workstation_id == ${workstationId})
        |> drop(columns: ["workstation_id"])
    
    // \`cols\`, \`present\`
    cols =
      base
        |> columns()
        |> keep(columns: ["_value"])
        |> distinct()
        |> findColumn(fn: (key) => true, column: "_value")
    
    present = array.filter(arr: fields, fn: (x) => contains(value: x, set: cols))
    missing = array.filter(arr: fields, fn: (x) => not contains(value: x, set: cols))
    
    default = -1
    
    // Timestamps "référence"
    times =
      base
        |> keep(columns: ["_time"])
        |> distinct()
    
    make = (col) =>
      base
        |> duplicate(column: col, as: "_value")
        |> map(fn: (r) => ({ r with _value: if exists r._value then r._value else default }))
        |> keep(columns: ["_time","_value"])
        |> set(key: "_field", value: col)
    
    // Série constante pour un champ absent
    makeConst = (col) =>
      times
        |> map(fn: (r) => ({ r with _value: default }))
        |> set(key: "_field", value: col)
    
    // On unionne les champs présents + les champs absents
    u = union(
      tables: array.map(arr: present, fn: (x) => make(col: x))
        |> array.concat(v: array.map(arr: missing, fn: (x) => makeConst(col: x)))
    )
    
    meansLong = u
        |> group(columns: ["_field"])
        |> aggregateWindow(every: ${window}, fn: mean, createEmpty: false)
    
    maxsLong = u
        |> group(columns: ["_field"])
        |> aggregateWindow(every: ${window}, fn: max, createEmpty: false)
        // renomme dynamiquement les champs max en step_X_max
        |> map(fn: (r) => ({ r with _field: r._field + "_max" }))
        |> map(fn: (r) => ({ r with _value: float(v: r._value) }))
    
    // union au format long -> pivot en colonnes step_X (mean) et step_X_max (max)
    union(
      tables: [meansLong] |> array.concat(v: [maxsLong])
    )
      |> group(columns: [])
      |> pivot(rowKey: ["_time"], columnKey: ["_field"], valueColumn: "_value")
      |> rename(columns: {_time: "time"})
  `;
};

export const getExpositionQuery = (workstationId, fieldsThresholds) => {
  return `
    import "array"
    import j "join"
    import "math"
    
    thresholds = array.from(rows: ${JSON.stringify(fieldsThresholds).replace(/"threshold":(\d+)([,\}])/g, '"threshold":$1.0$2')})
    
    base = from(bucket: "engine")
      |> range(start: v.timeRangeStart, stop: v.timeRangeStop)
      |> filter(fn: (r) => r._measurement == "reba_score")
      |> filter(fn: (r) => r._field == "workstation_id" or r._field =~ /^step_/)
      |> keep(columns: ["_time","_field","_value"])
    
    // filtre sur workstation_id
    workstationFilter = base
      |> filter(fn: (r) => r._field == "workstation_id" and r._value == ${workstationId})
      |> keep(columns: ["_time"])  // clé(s) de jointure
    
    // selectionner les steps
    stepsOnly = base
      |> filter(fn: (r) => r._field =~ /^step_/)
      |> group()
      |> sort(columns: ["_time"], desc: false)
    
    data = j.inner(left: stepsOnly, right: workstationFilter, on: (l, r) => l._time == r._time, as: (l, r) => l)
    
    // associe la ligne de seuil correspondante à chaque field (join sur _field)
    joined = join(tables: {d: data, t: thresholds}, on: ["_field"])
    
    // marque 1 si dépassement, sinon 0
    flagged = joined
      |> map(fn: (r) => ({r with exceed: if r._value >= r.threshold then 1 else 0, total: 1}))
      |> keep(columns: ["_time","_field","exceed","total"])
    
    // compte le nombre de dépassements par field (sur toute la période)
    flagged
      |> group(columns: ["_field"])
      |> reduce(identity: {exceed: 0, total: 0}, fn: (r, accumulator) => ({ exceed: accumulator.exceed + r.exceed, total: accumulator.total + r.total}))
      |> map(fn: (r) => ({r with percent: math.round(x: float(v: r.exceed) / float(v: r.total) * 100.0)}))
  `;
};

export const getMaxQuery = (workstationId, fields, window, side) => {
  const sideFields = fields.map((field) => field.startsWith('step_') ? field + '_' + side : field);

  return `
    import "array"
    import "strings"
    import "regexp"
    
    fields = ${JSON.stringify(sideFields)}
    pat    = "^(" + strings.joinStr(arr: fields, v: "|") + ")$"
    re     = regexp.compile(v: pat)
    
    base =
      from(bucket: "engine")
        |> range(start: v.timeRangeStart, stop: v.timeRangeStop)
        |> filter(fn: (r) => r._measurement == "reba_score")
        |> filter(fn: (r) => r._field == "workstation_id" or r._field =~ re)
        |> pivot(rowKey: ["_time"], columnKey: ["_field"], valueColumn: "_value")
        |> filter(fn: (r) => r.workstation_id == ${workstationId})
        |> drop(columns: ["workstation_id"])
    
    // \`cols\`, \`present\`
    cols =
      base
        |> columns()
        |> keep(columns: ["_value"])
        |> distinct()
        |> findColumn(fn: (key) => true, column: "_value")
    
    present = array.filter(arr: fields, fn: (x) => contains(value: x, set: cols))
    missing = array.filter(arr: fields, fn: (x) => not contains(value: x, set: cols))
    
    default = -1
    
    // Timestamps "référence"
    times =
      base
        |> keep(columns: ["_time"])
        |> distinct()
    
    make = (col) =>
      base
        |> duplicate(column: col, as: "_value")
        |> map(fn: (r) => ({ r with _value: if exists r._value then r._value else default }))
        |> keep(columns: ["_time","_value"])
        |> set(key: "_field", value: col)
    
    // Série constante pour un champ absent
    makeConst = (col) =>
      times
        |> map(fn: (r) => ({ r with _value: default }))
        |> set(key: "_field", value: col)
    
    // On unionne les champs présents + les champs absents
    union(
      tables: array.map(arr: present, fn: (x) => make(col: x))
        |> array.concat(v: array.map(arr: missing, fn: (x) => makeConst(col: x)))
    )
      |> group(columns: ["_field"])
      |> max()
  `;
};

export const formatSeriesByField = (data, fieldsNames, fill = 0) => {
  data = data.map(obj => Object.fromEntries(Object.entries(obj).map(([k,v]) => [k.replace(/_left$|_right$/, ''), v])));
  const expandedFieldsNames = fieldsNames.flatMap(item => item.startsWith("step_") ? [item, item + "_max"] : [item]);
  const out = Object.fromEntries(expandedFieldsNames.map(k => [k, []]));

  for (const row of data) {
    for (const k of expandedFieldsNames) {
      const v = row[k];
      out[k].push({time: row.time, value: typeof v === "number" ? v : fill});
    }
  }
  return out;
};

export const formatExpositionData = (data, fieldsNames) => {
  const out = Object.fromEntries(fieldsNames.map(k => [k, 0]));

  for (const row of data)
    out[row._field] = row.percent;

  return out;
};

export const formatMaxData = (data, fieldsNames) => {
  const out = Object.fromEntries(fieldsNames.map(k => [k, 0]));

  for (const row of data)
    out[row._field] = row._value;

  return out;
};

export const formatSeriesByShift = (data, seriesName) => {
  const formatted = data.map((point) => ({
    shift: point.shift,
    time: point._time,
    value: point[seriesName],
  }));

  const morning = formatted.filter(p => p.shift === "morning");
  const afternoon = formatted.filter(p => p.shift === "afternoon");

  return { morning, afternoon };
};